// In-memory database for the local mock adapter (mock/server.js). Seeded
// 1:1 from the example ids/values in docs/real-estate/be/10-api-reference.md
// §0.6 and its endpoint examples, so a screen built against this mock shows
// numbers that match the spec's own worked examples. Snake_case keys and
// UUID ids throughout, unlike the old store/modules/realEstate.js — this
// *is* the API shape, not a shape that gets translated into it later.
//
// Mutated in place by mock/server.js; nothing outside mock/ should import
// this directly.

let nextId = 1000;
export function newId(prefix) {
  nextId += 1;
  // Not a real UUID (doesn't need to be — nothing parses these as UUIDs,
  // they just need to be unique strings shaped like the real ids so a
  // component can't accidentally rely on a numeric-id assumption).
  return `${prefix}-mock-${nextId.toString(16).padStart(8, '0')}`;
}

const TCO = '3f6c1a2e-8b4d-4c7a-9e21-5d0b7a9c4e11';
const KAIA = 'a41d7e90-2c3b-4f8e-b6a5-19e0c7d2f833';
const UNIT_A1 = '0b8e5c2d-6f1a-4d3e-9c7b-2a4f8e1d6c90';
const UNIT_C = '5d2a9f7e-1c3b-4e6a-8d0f-7b9c2e4a1f35';
const UNIT_E = '9e4c1b7a-2d5f-4a8e-b3c6-0f1d7e9a2b48';
const ALI = '6a1f3e9c-4b2d-4f7a-8e0c-3d5b9a1c7e22';
const MEI = 'b7d2e4a1-9c3f-4e5b-a6d8-1f0c2e7b9a64';
const RAVI = 'e3c9a7b5-0d1f-4b2e-9a8c-6f4d1b3e5a07';

// Two rows (a lunch-break split shift) repeated Mon–Sat, closed Sunday —
// matches "open Mon–Sat 10:00–13:00 and 14:00–18:00, closed Sunday" (§0.6).
function weekdayHours(daysOfWeek, rows) {
  const hours = [];
  daysOfWeek.forEach(day => {
    rows.forEach(row => {
      hours.push({ id: newId('hour'), day_of_week: day, ...row });
    });
  });
  return hours;
}

export function seed() {
  return {
    properties: [
      {
        property_id: TCO,
        project_code: 'TCO',
        name: 'The Connaught One',
        developer: 'UEM Sunrise',
        short_description: 'Serviced apartments beside Taman Connaught',
        long_description:
          'A 1- to 4-bedroom serviced-apartment development next to Taman Connaught MRT station.',
        property_type: 'Residential',
        property_subtype: 'Service Apartment',
        project_status: 'open',
        marketing_badge: 'Selling Fast',
        is_active: true,
        currency: 'RM',
        website_url: 'https://www.uemsunrise.com/the-connaught-one',
        area: 'Taman Connaught, Cheras',
        region: 'Kuala Lumpur',
        address: 'Jalan Cerdas, Taman Connaught, 56000 Kuala Lumpur',
        latitude: 3.0806,
        longitude: 101.7394,
        tenure: 'Leasehold',
        buyer_restriction: null,
        showroom_address:
          'TCO Sales Gallery, Jalan Cerdas, Taman Connaught, 56000 Kuala Lumpur',
        showroom_phone: '+60 3-2727 6868',
        showroom_waze_url: 'https://waze.com/ul?ll=3.0806,101.7394',
        showroom_maps_url: 'https://maps.google.com/?q=3.0806,101.7394',
        booking_mode: 'agent',
        viewing_duration_minutes: 60,
        slot_capacity: 3,
        timezone: 'Asia/Kuala_Lumpur',
        embedding_status: 'ready',
        created_by: 301,
        created_at: '2026-09-20T02:11:04Z',
        updated_at: '2026-09-27T08:40:19Z',
        agent_ids: [ALI, MEI, RAVI],
      },
      {
        property_id: KAIA,
        project_code: 'KAIA',
        name: 'Kaia Heights',
        developer: 'UEM Sunrise',
        short_description: 'Waterfront condominiums in Seri Kembangan',
        long_description: '',
        property_type: 'Residential',
        property_subtype: 'Condominium',
        project_status: 'upcoming',
        marketing_badge: null,
        is_active: true,
        currency: 'RM',
        website_url: '',
        area: 'Seri Kembangan',
        region: 'Selangor',
        address: '',
        latitude: null,
        longitude: null,
        tenure: 'Freehold',
        buyer_restriction: null,
        showroom_address:
          'Kaia Heights Sales Gallery, Jalan Equine, Seri Kembangan',
        showroom_phone: '+60 3-8942 1188',
        showroom_waze_url: '',
        showroom_maps_url: '',
        booking_mode: 'walk_in',
        viewing_duration_minutes: null,
        slot_capacity: null,
        timezone: 'Asia/Kuala_Lumpur',
        embedding_status: 'ready',
        created_by: 301,
        created_at: '2026-09-18T01:00:00Z',
        updated_at: '2026-09-18T01:00:00Z',
        agent_ids: [],
      },
    ],

    // { property_id -> [{ day_of_week, start_time, end_time }] } — Layer 0.
    operatingHours: {
      [TCO]: weekdayHours(
        [0, 1, 2, 3, 4, 5],
        [
          { start_time: '10:00', end_time: '13:00' },
          { start_time: '14:00', end_time: '18:00' },
        ]
      ),
      [KAIA]: weekdayHours(
        [0, 1, 2, 3, 4, 5, 6],
        [{ start_time: '10:00', end_time: '18:00' }]
      ),
    },
    // { property_id -> [{id, override_date, start_time, end_time, reason, closed}] } — M2.
    operatingHoursOverrides: { [TCO]: [], [KAIA]: [] },

    features: {
      [TCO]: [
        {
          amenity_id: newId('amenity'),
          name: 'Infinity pool',
          icon_url: null,
          display_order: 0,
        },
      ],
      [KAIA]: [],
    },
    nearby: {
      [TCO]: [
        {
          amenity_id: newId('amenity'),
          name: 'Eko Cheras Mall',
          distance_value: 2.7,
          distance_unit: 'km',
          maps_url: null,
          icon_url: null,
          display_order: 1,
        },
      ],
      [KAIA]: [],
    },
    brochures: {},
    gallery: {
      [TCO]: [
        {
          image_id: newId('image'),
          url: 'https://singabot-media.s3.ap-southeast-1.amazonaws.com/real_estate/mock/tco/g1.jpg',
          caption: 'Facade',
          display_order: 0,
        },
      ],
      [KAIA]: [],
    },
    cover: {
      [TCO]: {
        image_id: newId('image'),
        url: 'https://singabot-media.s3.ap-southeast-1.amazonaws.com/real_estate/mock/tco/cover.jpg',
        caption: null,
        display_order: 0,
      },
      [KAIA]: null,
    },

    units: [
      {
        unit_id: UNIT_A1,
        property_id: TCO,
        floor_plan_code: 'A1',
        display_order: 1,
        built_up_size: 468,
        built_up_unit: 'sqft',
        bedrooms: 1,
        bedrooms_extra: null,
        parking_lots: 1,
        bathrooms: 1,
        bathrooms_extra: null,
        price_from: 380000,
        currency: 'RM',
        website_url: 'https://example-developer.com/the-connaught-one/type-a1',
        layout_url: null,
        image_url:
          'https://singabot-media.s3.ap-southeast-1.amazonaws.com/real_estate/mock/tco/units/a1.jpg',
        created_at: '2026-09-20T02:15:00Z',
        updated_at: '2026-09-20T02:15:00Z',
      },
      {
        unit_id: UNIT_C,
        property_id: TCO,
        floor_plan_code: 'C',
        display_order: 2,
        built_up_size: 960,
        built_up_unit: 'sqft',
        bedrooms: 3,
        bedrooms_extra: null,
        parking_lots: 2,
        bathrooms: 2,
        bathrooms_extra: null,
        price_from: 605900,
        currency: 'RM',
        website_url: 'https://www.uemsunrise.com/the-connaught-one#type-c',
        layout_url: null,
        image_url: null,
        created_at: '2026-09-20T02:15:00Z',
        updated_at: '2026-09-20T02:15:00Z',
      },
      {
        unit_id: UNIT_E,
        property_id: TCO,
        floor_plan_code: 'E',
        display_order: 3,
        built_up_size: 118,
        built_up_unit: 'sqm',
        bedrooms: 4,
        bedrooms_extra: null,
        parking_lots: 2,
        bathrooms: 3,
        bathrooms_extra: null,
        price_from: 787900,
        currency: 'RM',
        website_url: '',
        layout_url: null,
        image_url: null,
        created_at: '2026-09-20T02:15:00Z',
        updated_at: '2026-09-20T02:15:00Z',
      },
    ],

    agents: [
      {
        agent_id: ALI,
        name: 'Ali Hassan',
        email: 'ali.hassan@uemsunrise.com',
        phone: '+60 12-345 6789',
        created_at: '2026-09-21T01:00:00Z',
        updated_at: '2026-09-21T01:00:00Z',
      },
      {
        agent_id: MEI,
        name: 'Mei Ling Tan',
        email: 'mei.ling@uemsunrise.com',
        phone: '+60 12-888 5566',
        created_at: '2026-09-21T01:00:00Z',
        updated_at: '2026-09-21T01:00:00Z',
      },
      {
        agent_id: RAVI,
        name: 'Ravi Kumar',
        email: 'ravi.kumar@uemsunrise.com',
        phone: '+60 16-222 3344',
        created_at: '2026-09-21T01:00:00Z',
        updated_at: '2026-09-21T01:00:00Z',
      },
    ],
    // Layer 2/3 — leave / regular schedules / one-off assignments. Empty
    // until M2; every assigned agent is treated as working the property's
    // Layer 0 opening hours in the meantime (mock/rules.js).
    unavailability: [],
    schedules: [],
    specificAssignments: [],

    appointments: [
      {
        appointment_id: '2c4e6a8b-0d1f-4a3c-9e5b-7d9f1b3d5f79',
        appointment_type: 'viewing',
        property_id: TCO,
        unit_id: UNIT_C,
        status: 'confirmed',
        date: '2026-10-06',
        start_time: '10:00',
        end_time: '11:00',
        duration_minutes: 60,
        agent_id: ALI,
        agent_auto_assigned: true,
        customer_id: 5012,
        customer_name: 'Aisyah Rahman',
        customer_phone: '+60123000111',
        conversation_id: 8841,
        location_type: 'showroom',
        location_note: null,
        source: 'bot',
        customer_notes: 'Interested in a high floor',
        agent_notes: null,
        handover_checklist: null,
        before_reschedule_metadata: null,
        cancelled_at: null,
        cancellation_reason: null,
        completed_at: null,
        created_by: null,
        created_at: '2026-09-28T04:00:00Z',
        updated_at: '2026-09-28T05:10:00Z',
      },
      {
        appointment_id: 'f1a2b3c4-d5e6-4f70-8a91-b2c3d4e5f607',
        appointment_type: 'viewing',
        property_id: KAIA,
        unit_id: null,
        status: 'requested',
        date: '2026-10-03',
        start_time: '11:00',
        end_time: null,
        duration_minutes: null,
        agent_id: null,
        agent_auto_assigned: false,
        customer_id: 5012,
        customer_name: 'Aisyah Rahman',
        customer_phone: '+60123000111',
        conversation_id: 8841,
        location_type: 'showroom',
        location_note: null,
        source: 'portal',
        customer_notes: 'Coming with parents',
        agent_notes: null,
        handover_checklist: null,
        before_reschedule_metadata: null,
        cancelled_at: null,
        cancellation_reason: null,
        completed_at: null,
        created_by: 301,
        created_at: '2026-09-25T03:00:00Z',
        updated_at: '2026-09-25T03:00:00Z',
      },
    ],

    importJobs: {},
  };
}

export const ids = { TCO, KAIA, UNIT_A1, UNIT_C, UNIT_E, ALI, MEI, RAVI };
